/**
 * @typedef {Object} OutputField
 * @property {string} name - Field identifier
 * @property {string|function} label - Display label or function returning label
 * @property {function} [checked] - Function determining if field is selected by default
 * @property {function} [visible] - Function determining if field should be shown
 * @property {boolean} [forceChecked] - If true, field is always selected and cannot be unchecked
 * @property {function} [disabled] - Function determining if fields should be disabled
 */

const campaignEffectivenessOutputFields = [
  {
    name: "firstNPI",
    label: "First NPI",
    disabled: ({ common }) => !!common.canBeVisualized,
    checked: ({ common }) =>
      !!common.outputs.firstNPI || !!common.canBeVisualized
  },
  {
    name: "firstPhysicianName",
    label: "First Physician Name",
    disabled: ({ common }) => !!common.canBeVisualized,
    checked: ({ common }) =>
      !!common.outputs.firstPhysicianName || !!common.canBeVisualized
  },
  {
    name: "firstPhysicianClassification",
    label: "First Physician Classification",
    disabled: ({ common }) => !!common.canBeVisualized,
    checked: ({ common }) =>
      !!common.outputs.firstPhysicianClassification || !!common.canBeVisualized
  },
  {
    name: "firstPhysicianSpecialization",
    label: "First Physician Specialization",
    disabled: ({ common }) => !!common.canBeVisualized,
    checked: ({ common }) =>
      !!common.outputs.firstPhysicianSpecialization || !!common.canBeVisualized
  },

  {
    name: "newPatients",
    label: "New Patient (Timeframe 2)",
    disabled: ({ common }) => !!common.canBeVisualized,
    checked: ({ common }) =>
      !!common.outputs.newPatients || !!common.canBeVisualized
  },

  {
    name: "secondNPI",
    label: "Second Physician NPI",
    disabled: ({ common }) => !!common.canBeVisualized,
    checked: ({ common }) =>
      !!common.outputs.secondNPI || !!common.canBeVisualized
  },
  {
    name: "secondPhysicianName",
    label: "Second Physician Name",
    disabled: ({ common }) => !!common.canBeVisualized,
    checked: ({ common }) =>
      !!common.outputs.secondPhysicianName || !!common.canBeVisualized
  },
  {
    name: "secondPhysicianClassification",
    label: "Second Physician Classification",
    disabled: ({ common }) => !!common.canBeVisualized,
    checked: ({ common }) =>
      !!common.outputs.secondPhysicianClassification || !!common.canBeVisualized
  },
  {
    name: "secondPhysicianSpecialization",
    label: "Second Physician Specialization",
    disabled: ({ common }) => !!common.canBeVisualized,
    checked: ({ common }) =>
      !!common.outputs.secondPhysicianSpecialization || !!common.canBeVisualized
  },

  {
    name: "timeframe",
    label: "Timeframe",
    disabled: ({ common }) => !!common.canBeVisualized,
    checked: ({ common }) =>
      !!common.outputs.timeframe || !!common.canBeVisualized
  },
  {
    name: "procedureCode",
    label: "Procedure Code",
    disabled: ({ common }) => !!common.canBeVisualized,
    checked: ({ common }) =>
      !!common.outputs.procedureCode || !!common.canBeVisualized
  },
  {
    name: "procedureDescription",
    label: "Procedure Description",
    disabled: ({ common }) => !!common.canBeVisualized,
    checked: ({ common }) =>
      !!common.outputs.procedureDescription || !!common.canBeVisualized
  },
  {
    name: "proceduresCount",
    label: "Count of Procedures",
    disabled: ({ common }) => !!common.canBeVisualized,
    checked: ({ common }) =>
      !!common.outputs.proceduresCount || !!common.canBeVisualized
  },
  {
    name: "sharedPatients",
    label: "Shared Patients",
    disabled: ({ common }) => !!common.canBeVisualized,
    checked: ({ common }) =>
      !!common.outputs.sharedPatients || !!common.canBeVisualized
  },
  {
    name: "sumCharges",
    label: "Sum of Charges",
    disabled: ({ common }) => !!common.canBeVisualized,
    checked: ({ common }) =>
      !!common.outputs.sumCharges || !!common.canBeVisualized
  },

  {
    name: "firstOrgNPI",
    label: "First Organization NPI",
    visible: ({ common }) => !!common.includeFacilities,
    disabled: ({ common }) => !!common.canBeVisualized,
    checked: ({ common }) =>
      !!common.outputs.firstOrgNPI || !!common.canBeVisualized
  },
  {
    name: "firstOrgName",
    label: "First Organization Name",
    visible: ({ common }) => !!common.includeFacilities,
    disabled: ({ common }) => !!common.canBeVisualized,
    checked: ({ common }) =>
      !!common.outputs.firstOrgName || !!common.canBeVisualized
  },
  {
    name: "firstOrgClassification",
    label: "First Organization Classification",
    visible: ({ common }) => !!common.includeFacilities,
    disabled: ({ common }) => !!common.canBeVisualized,
    checked: ({ common }) =>
      !!common.outputs.firstOrgClassification || !!common.canBeVisualized
  },
  {
    name: "firstOrgSpecialization",
    label: "First Organization Specialization",
    visible: ({ common }) => !!common.includeFacilities,
    disabled: ({ common }) => !!common.canBeVisualized,
    checked: ({ common }) =>
      !!common.outputs.firstOrgSpecialization || !!common.canBeVisualized
  },
  {
    name: "secondOrgNPI",
    label: "Second Organization NPI",
    visible: ({ common }) => !!common.includeFacilities,
    disabled: ({ common }) => !!common.canBeVisualized,
    checked: ({ common }) =>
      !!common.outputs.secondOrgNPI || !!common.canBeVisualized
  },
  {
    name: "secondOrgName",
    label: "Second Organization Name",
    visible: ({ common }) => !!common.includeFacilities,
    disabled: ({ common }) => !!common.canBeVisualized,
    checked: ({ common }) =>
      !!common.outputs.secondOrgName || !!common.canBeVisualized
  },
  {
    name: "secondOrgClassification",
    label: "Second Organization Classification",
    visible: ({ common }) => !!common.includeFacilities,
    disabled: ({ common }) => !!common.canBeVisualized,
    checked: ({ common }) =>
      !!common.outputs.secondOrgClassification || !!common.canBeVisualized
  },
  {
    name: "secondOrgSpecialization",
    label: "Second Organization Specialization",
    visible: ({ common }) => !!common.includeFacilities,
    disabled: ({ common }) => !!common.canBeVisualized,
    checked: ({ common }) =>
      !!common.outputs.secondOrgSpecialization || !!common.canBeVisualized
  }
];

export default campaignEffectivenessOutputFields;
